import styled from '@emotion/styled'
import React, { type FunctionComponent, useCallback, useRef, useState } from 'react'
import {
  borderRadius,
  colors,
  EINA_01_FONT,
  fontSize,
  fontWeight,
  spacing,
} from '@retailer-platform/shared-components'
import { WalkIcon, TrendingIcon, CartIcon, elevation } from '@instacart/ids-core'
import moment from 'moment'
import { NotifierIconWithCounts } from 'domains/shifts/components/components/staffing-alert/NotifierIconWithCounts'
import { useDomainMessages } from 'domains/shifts/utils/domain/intl'
import { StaffingAlertTimePill } from 'domains/shifts/components/components/staffing-alert/StaffingAlertTimePill'
import { RecommendationTypeEnum } from 'domains/shifts/api'
import { type RecommendationShiftType } from 'domains/shifts/components/contexts/RecommendationShiftsContext'

type InactiveStyleProp = {
  inactive?: boolean
  isTooltipVisible?: boolean
}
const CardContainer = styled.div<InactiveStyleProp>(({ inactive, isTooltipVisible }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  minWidth: '300px',
  maxWidth: '500px',
  height: '42px',
  padding: `${spacing.X8} ${spacing.X12}`,
  boxShadow: inactive ? 'none' : elevation.low.shadow.boxShadow,
  borderRadius: borderRadius.X12,
  margin: `${spacing.X8} ${spacing.X8}`,
  border: `2px solid ${inactive ? colors.GRAYSCALE.X10 : 'transparent'}`,
  borderColor: inactive
    ? colors.GRAYSCALE.X10
    : isTooltipVisible
    ? colors.GRAYSCALE.X70
    : 'transparent',
  cursor: inactive ? 'default' : 'pointer',
  '&:hover': {
    backgroundColor: inactive ? colors.GRAYSCALE.X0 : colors.GRAYSCALE.X10,
  },
}))

const StyledPopper = styled.div({
  position: 'absolute',
  top: '100%',
  left: 0,
  backgroundColor: colors.GRAYSCALE.X0,
  color: colors.SECONDARY.LIGHT,
  borderRadius: borderRadius.X4,
  padding: `${spacing.X8} ${spacing.X12}`,
  boxShadow: elevation.low.shadow.boxShadow,
  fontSize: fontSize.X12,
  minHeight: '165px',
  lineHeight: '1.5em',
  zIndex: 101,
  margin: `0 ${spacing.X8}`,
  minWidth: '300px',
  maxWidth: '500px',
})

const CardIcon = styled.div({
  height: spacing.X20,
  width: spacing.X20,
  margin: `${spacing.X0} ${spacing.X8}`,
})

const CardText = styled.span<InactiveStyleProp>(props => ({
  fontFamily: EINA_01_FONT,
  fontWeight: fontWeight.REGULAR,
  fontSize: fontSize.X15,
  marginLeft: spacing.X4,
  color: props.inactive ? colors.GRAYSCALE.X50 : colors.GRAYSCALE.X70,
}))
const CardGroup = styled.div({ display: 'flex', flexDirection: 'row', alignItems: 'center' })

const PopupTitle = styled(CardText)({
  fontWeight: fontWeight.BOLD,
  fontSize: fontSize.X18,
  lineHeight: '26px',
  display: 'block',
  marginLeft: 0,
})
const PopupSubtitle = styled(CardText)({
  display: 'block',
  fontSize: fontSize.X12,
  fontWeight: fontWeight.REGULAR,
  lineHeight: fontSize.X18,
  color: colors.GRAYSCALE.X50,
  marginLeft: 0,
})

const TimePillGroup = styled.div({
  display: 'flex',
  flex: 1,
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'flex-start',
  marginTop: spacing.X24,
})

interface Props {
  alertCount: number
  recommendationType: RecommendationTypeEnum
  recommendations: RecommendationShiftType[]
  zoneOffset: number
}

export const getIconByType = (
  recommendationType: RecommendationTypeEnum,
  inactive: boolean,
  primaryColor: string = colors.GRAYSCALE.X70,
  secondaryColor: string = colors.GRAYSCALE.X30
) => {
  const fill = inactive ? secondaryColor : primaryColor
  const iconProps = {
    size: spacing.X20,
    fill,
  }
  switch (recommendationType) {
    case RecommendationTypeEnum.DemandSurge:
      return <TrendingIcon {...iconProps} />
    case RecommendationTypeEnum.NoRunner:
      return <WalkIcon {...iconProps} />
    default:
      return <CartIcon {...iconProps} />
  }
}
export const getTitleByType = (
  recommendationType: RecommendationTypeEnum,
  messages: Record<'demandSurge' | 'noRunner', string>
) => {
  switch (recommendationType) {
    case RecommendationTypeEnum.DemandSurge:
      return messages.demandSurge
    case RecommendationTypeEnum.NoRunner:
      return messages.noRunner
    default:
      return ''
  }
}

export const StaffingAlertCard: FunctionComponent<Props> = ({
  alertCount,
  recommendationType,
  recommendations,
  zoneOffset,
}) => {
  const messages = useDomainMessages({
    demandSurge: 'shiftsDomain.staffingAlert.demandSurge',
    noRunner: 'shiftsDomain.staffingAlert.noRunner',
    viewDetails: 'shiftsDomain.staffingAlert.viewDetails',
    demandSurgeTitle: 'shiftsDomain.staffingAlert.demandSurgePopupTitle',
    demandSurgeSubtitle: 'shiftsDomain.staffingAlert.demandSurgePopupSubtitle',
    noRunnerTitle: 'shiftsDomain.staffingAlert.noRunnerPopupTitle',
    noRunnerSubtitle: 'shiftsDomain.staffingAlert.noRunnerPopupSubtitle',
  })

  const popupCardTitle: { [key in RecommendationTypeEnum]: string } = {
    [RecommendationTypeEnum.NoRunner]: messages.noRunnerTitle,
    [RecommendationTypeEnum.DemandSurge]: messages.demandSurgeTitle,
  }

  const popupCardSubtitle: { [key in RecommendationTypeEnum]: string } = {
    [RecommendationTypeEnum.NoRunner]: messages.noRunnerSubtitle,
    [RecommendationTypeEnum.DemandSurge]: messages.demandSurgeSubtitle,
  }

  const TIME_FORMAT = 'ha'
  const staffingAlertTimePillArray = recommendations.map(({ startsAt, endsAt }) => {
    const formattedStart = moment.utc(startsAt).add(zoneOffset, 'h').format(TIME_FORMAT)
    const formattedEnd = moment.utc(endsAt).add(zoneOffset, 'h').format(TIME_FORMAT)
    return `${formattedStart}-${formattedEnd}`
  })

  const inactive = alertCount === 0

  const [visible, setVisible] = useState(false)
  const toggleTooltip = useCallback(
    () => !inactive && setVisible(!visible),
    [setVisible, visible, inactive]
  )

  const referenceElementRef = useRef<HTMLDivElement>(null)

  return (
    <div
      ref={referenceElementRef}
      onMouseOver={toggleTooltip}
      onMouseOut={toggleTooltip}
      data-testid={`staffingAlertCard_${recommendationType}`}
      style={{ flex: 1, position: 'relative' }}
    >
      <CardContainer isTooltipVisible={visible} inactive={inactive}>
        <CardGroup>
          <NotifierIconWithCounts count={alertCount} />
          <CardIcon>{getIconByType(recommendationType, inactive)}</CardIcon>
          <CardText inactive={inactive}>{getTitleByType(recommendationType, messages)}</CardText>
        </CardGroup>
        {alertCount > 0 && (
          <CardText
            style={{
              color: visible ? colors.GRAYSCALE.X30 : colors.PRIMARY.REGULAR,
              fontWeight: fontWeight.SEMIBOLD,
            }}
          >
            {messages.viewDetails}
          </CardText>
        )}
        {visible && (
          <StyledPopper>
            <PopupTitle>{popupCardTitle[recommendationType]}</PopupTitle>
            <PopupSubtitle>{popupCardSubtitle[recommendationType]}</PopupSubtitle>
            <TimePillGroup>
              {staffingAlertTimePillArray.map((timeText, index) => (
                <StaffingAlertTimePill text={timeText} key={index} />
              ))}
            </TimePillGroup>
          </StyledPopper>
        )}
      </CardContainer>
    </div>
  )
}
